export const STOCK_METRICS = ['bad', 'good', 'required', 'calls', 'eligible'];
export const STOCK_LABELS = { bad: 'Bad stock', good: 'Good stock', required: 'Repair required', calls: 'Eligible calls', eligible: 'Parsed backstock' };
export const emptyMetrics = () => Object.fromEntries(STOCK_METRICS.map(k => [k, 0]));
export const stockTeam = state => String((state.teamFilter === 'CUSTOM' ? state.customTeamFilter : state.teamFilter) || '').trim().toUpperCase();

export function stableJSON(value) {
  return JSON.stringify(value, (_, v) => v instanceof Set ? [...v].sort() : v && typeof v === 'object' && !Array.isArray(v) ? Object.fromEntries(Object.keys(v).sort().map(k => [k, v[k]])) : v);
}
export async function sha256(value) {
  const bytes = typeof value === 'string' ? new TextEncoder().encode(value) : value;
  return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), b => b.toString(16).padStart(2, '0')).join('');
}
export function sourceParts(kind, rows) {
  const parts = new Map();
  for (const row of rows) {
    const id = String(row.partId || row.ref || row.serial || '').trim().toUpperCase();
    if (!id) continue;
    if (!parts.has(id)) parts.set(id, { part_id: id, ...emptyMetrics() });
    const p = parts.get(id);
    if (kind === 'calls') p.calls += 1;
    else { p.bad += Number(row.bad) || 0; p.good += Number(row.good) || 0; p.required += Number(row.repReq) || 0; }
  }
  return [...parts.values()].sort((a,b) => a.part_id.localeCompare(b.part_id));
}
export function parsedParts(report) {
  return report.summary.map(r => ({ part_id:r.partId, ...emptyMetrics(), required:r.repairRequiredCount, calls:r.callListCount, eligible:r.backstockCount }));
}
const stockLondonDay = new Intl.DateTimeFormat('en-CA', { timeZone:'Europe/London', year:'numeric', month:'2-digit', day:'2-digit' });
export function londonDay(value) { return stockLondonDay.format(new Date(value)); }

// Input includes the last report before the requested range. Every report is a
// complete snapshot; no interpolation, assumed deliveries or fulfilled calls.
export function buildPeriods(snapshots, grain = 'day', from = '', to = '') {
  const periods = new Map();
  let previous = null;
  for (const s of [...snapshots].sort((a,b) => a.observed_at.localeCompare(b.observed_at) || (a.created_at || '').localeCompare(b.created_at || '') || String(a.id).localeCompare(String(b.id)))) {
    const date = londonDay(s.observed_at);
    const value = { ...emptyMetrics(), ...s.totals };
    const comparable = previous && s.kind !== 'parsed' && previous.scope_hash === s.scope_hash && previous.team === s.team && previous.kind === s.kind;
    if ((!from || date >= from) && (!to || date <= to)) {
      const key = date.slice(0, grain === 'year' ? 4 : grain === 'month' ? 7 : 10);
      if (!periods.has(key)) periods.set(key, { key, count:0, baselines:0, comparisons:0, open:comparable ? {...emptyMetrics(),...previous.totals} : value, close:value, change:emptyMetrics(), increase:emptyMetrics(), decrease:emptyMetrics(), low:{...value}, high:{...value}, first:s.observed_at, last:s.observed_at });
      const p = periods.get(key);
      p.count++; p.close = value; p.last = s.observed_at;
      if (comparable) p.comparisons++; else p.baselines++;
      for (const k of STOCK_METRICS) {
        const delta = comparable ? value[k] - (Number(previous.totals[k]) || 0) : 0;
        p.change[k] += delta; p.increase[k] += Math.max(0,delta); p.decrease[k] += Math.max(0,-delta);
        p.low[k] = Math.min(p.low[k],value[k]); p.high[k] = Math.max(p.high[k],value[k]);
      }
    }
    previous = { ...s, totals:value };
  }
  return [...periods.values()];
}

export function stockConfiguration(state, mapping = {}) {
  return { version:1, mapping, clients:[...(state.clients || [])].sort((a,b)=>String(a.id).localeCompare(String(b.id))), references:state.clientPartIds || {}, referenceTeam:state.clientPartIdsTeam || '', grading:state.gradingLookup || {}, gradeValues:state.gradeValues || {} };
}

export function stockCSV(rows) {
  if (!rows.length) return '';
  const quote = value => '"' + String(value ?? '').replace(/^[=+@-]/, "'$&").replace(/"/g,'""') + '"';
  const keys = Object.keys(rows[0]);
  return [keys.map(quote).join(','), ...rows.map(r => keys.map(k => quote(r[k])).join(','))].join('\r\n');
}
export function downloadStockFile(data, name) {
  const url = URL.createObjectURL(data instanceof Blob ? data : new Blob([data], {type:'application/json'}));
  const a = document.createElement('a'); a.href=url; a.download=name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
